import { motion } from 'motion/react';
import type { ComponentType } from 'react';
import { DIAL_TRANSITION } from 'src/lib/motion.ts';
import { ResultType } from 'src/types/types.ts';

/**
 * Compact score ring (donut) with the level icon in the center instead of a
 * number. Arc length encodes score/5, colored by the maturity tone. Shares the
 * SVG math with ScoreDial but is sized for inline use inside a level card.
 */
export function ScoreRing({
  value,
  tone,
  Icon,
  size = 64,
  stroke = 6,
  animate = true,
  className = ''
}: {
  value: number;
  tone: ResultType['tone'];
  Icon: ComponentType<{ className?: string }>;
  size?: number;
  stroke?: number;
  animate?: boolean;
  className?: string;
}) {
  const c = size / 2;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, value / 5));
  const dash = circ * pct;
  const color = `var(--zeb-tone-${tone})`;

  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={c} cy={c} r={r} fill="none" stroke="var(--zeb-muted)" strokeWidth={stroke} />
        <motion.circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          transform={`rotate(-90 ${c} ${c})`}
          initial={{ strokeDasharray: `0 ${circ}` }}
          animate={{ strokeDasharray: `${dash} ${circ}` }}
          transition={animate ? DIAL_TRANSITION : { duration: 0 }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon className="h-5 w-5 text-zeb-tertiary" />
      </div>
    </div>
  );
}
